import { useState, type Dispatch, type SetStateAction } from "react";
import { errorText } from "../../i18n/serverErrors";
import { shopModeApi, type FloorSaleResult } from "../../api/shopMode";
import type { BelowFloorFlag } from "../../api/types";
import {
  NO_PAYMENT_CHOICE,
  lineCents,
  readPaymentChoice,
  type PaymentChoice,
} from "../../utils/paymentSplit";
import { ShopFloorPaymentSplit, paymentProblemText } from "./ShopFloorPaymentSplit";
import { BelowFloorPrompt } from "./BelowFloorPrompt";
import { useBoxPickPrompt } from "./useBoxPickPrompt";
import { SaleCompleteFlow } from "./SaleCompleteFlow";
import {
  TicketLines,
  ticketLinesProblem,
  type TicketLine,
} from "./TicketLines";
import { eur } from "./shopFloorFormat";
import { useT } from "../../i18n/LanguageProvider";

/** Re-exported so the builder (which owns the ticket) shares one shape. */
export type InlineSellLine = TicketLine;

/**
 * G-46 GAP 2 — the walk-in sale completes INSIDE the bouquet panel: price
 * charged + Cash/Card + Sell under the totals the florist is already
 * looking at (owner amendment: one flow, never a screen change, never a
 * link out to the till). The item FINDER lives in the builder's wide canvas
 * (2026-08-09 layout call); this component holds only the money and the
 * ticket, on the shared TicketLines/SaleConfirmationOverlay pieces so the
 * till and the checkout can never drift. OD-18's 2.5 s confirmation timing
 * lives inside the overlay and is not touched here.
 */
export function ShopFloorInlineSell({
  build,
  lines,
  onLinesChange,
  onSold,
}: {
  build: {
    buildId: string;
    name: string | null;
    suggestedSalePriceEur: string | null;
  };
  lines: InlineSellLine[];
  onLinesChange: Dispatch<SetStateAction<InlineSellLine[]>>;
  onSold?: () => void;
}) {
  const { t } = useT();
  const [priceStr, setPriceStr] = useState(build.suggestedSalePriceEur ?? "");
  // G-141 (OD-215): Cash, Card, or both with the cash part typed.
  const [payment, setPayment] = useState<PaymentChoice>(NO_PAYMENT_CHOICE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sold, setSold] = useState<FloorSaleResult | null>(null);
  // G-45 part 3: kept OUTSIDE `sold` — the 2.5 s confirmation dismisses
  // itself (OD-18) and must not take the florist's prompt with it.
  const [belowFloorFlags, setBelowFloorFlags] = useState<BelowFloorFlag[]>([]);
  const boxPrompt = useBoxPickPrompt();

  // Cents, each line rounded as the server rounds it (G-141).
  const totalCents =
    lineCents(Number(priceStr)) +
    lines.reduce((sum, l) => sum + lineCents(Number(l.priceStr), Number(l.qtyStr) || 0), 0);
  const total = totalCents / 100;

  const sell = async () => {
    if (priceStr.trim() === "" || !Number.isFinite(Number(priceStr))) {
      setError(t("inlineSell.enterPrice"));
      return;
    }
    const paid = readPaymentChoice(payment, totalCents);
    if (!paid.ok) {
      // A split's own problem is already shown under the two boxes.
      setError(paid.problem === "pickMethod" ? paymentProblemText(paid.problem, totalCents, t) : null);
      return;
    }
    const ticketProblem = ticketLinesProblem(lines, { requirePrices: true });
    if (ticketProblem) {
      setError(ticketProblem);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await boxPrompt.run((boxAnswers) =>
        shopModeApi.floorCreateSale({
          payments: paid.parts,
          lines: [
            {
              kind: "BOUQUET",
              bouquetBuildId: build.buildId,
              quantity: 1,
              unitPriceEur: Number(priceStr),
            },
            ...lines.map((l) => ({
              kind: "ITEM" as const,
              itemId: l.item.id,
              quantity: Number(l.qtyStr),
              unitPriceEur: Number(l.priceStr),
            })),
          ],
          ...(boxAnswers.length ? { boxAnswers } : {}),
        }),
      );
      // G-156: the below-floor prompt waits until the receipt question and
      // the earnings card are over (OD-34's order).
      setSold(result);
    } catch (err: unknown) {
      setError(errorText(err, "sale.couldNotLog"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sf-inline-sell">
      {boxPrompt.element}
      {belowFloorFlags.length > 0 ? (
        <BelowFloorPrompt
          flags={belowFloorFlags}
          onDone={() => setBelowFloorFlags([])}
        />
      ) : null}
      {sold ? (
        <SaleCompleteFlow
          saleId={sold.id}
          commissionPercent={sold.commissionPercent}
          commissionEarnedEur={sold.commissionEarnedEur}
          summary={`${eur(Number(sold.totalEur))} · ${t("sale.lines", { count: sold.lineCount })}`}
          onDone={() => {
            setBelowFloorFlags(sold.belowFloor ?? []);
            setSold(null);
            onSold?.();
          }}
        />
      ) : null}

      {error ? <div className="alert error">{error}</div> : null}

      <div className="sf-inline-sell-row">
        <div className="field">
          <label htmlFor="sf-inline-price">{t("inlineSell.priceCharged")}</label>
          <input
            id="sf-inline-price"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={priceStr}
            onChange={(e) => setPriceStr(e.target.value)}
            disabled={busy}
          />
        </div>
        <ShopFloorPaymentSplit
          value={payment}
          onChange={setPayment}
          totalCents={totalCents}
          idPrefix="sf-inline"
          disabled={busy}
        />
      </div>

      <TicketLines lines={lines} onLinesChange={onLinesChange} disabled={busy} />

      <div className="button-row" style={{ alignItems: "center" }}>
        <button
          type="button"
          className="button sf-bouquet-sell-btn"
          onClick={() => void sell()}
          disabled={busy || sold != null}
        >
          {busy ? t("inlineSell.selling") : t("inlineSell.sell", { total: eur(total) })}
        </button>
      </div>
    </div>
  );
}
